import React from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function useDialogFocusManagement({ busy = false, onClose, open = true }) {
  const dialogRef = React.useRef(null);
  const initialFocusRef = React.useRef(null);
  const busyRef = React.useRef(busy);
  const onCloseRef = React.useRef(onClose);
  busyRef.current = busy;
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const opener = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const focusables = () => dialogFocusableElements(dialog);
    const preferredTarget = initialFocusRef.current;
    const initialTarget = preferredTarget && !preferredTarget.disabled
      ? preferredTarget
      : focusables()[0] || dialog;
    initialTarget.focus();

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        if (busyRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusables();
      if (elements.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  const handleBackdropClick = React.useCallback(event => {
    if (event.target !== event.currentTarget || busyRef.current) return;
    onCloseRef.current?.();
  }, []);

  return { dialogRef, handleBackdropClick, initialFocusRef };
}

function dialogFocusableElements(dialog) {
  return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(element => (
    !element.hidden &&
    !element.closest('[hidden]') &&
    element.getAttribute('aria-hidden') !== 'true'
  ));
}
